/**
 * @file AppHeader.tsx
 * @description The main application header component, providing navigation, user menu,
 * and the ProfileSwitcher for trainer users.
 */

import { SESSION_PROPOSALS_ENABLED } from "../utils/featureFlags";
import { Button } from "./ui/button";
import {
  Home,
  List,
  BarChart3,
  Award,
  Settings,
  LogOut,
  User,
  Users,
  Calendar,
  Shield,
  Menu,
} from "lucide-react";
import TitusLogo from "./icon/TitusLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { useAuth } from "../contexts/AuthContext";
import { ProfileSwitcher } from "./profile/ProfileSwitcher";
import { useViewMode } from "../contexts/ViewModeContext";
import { Link, useNavigate, useLocation } from 'react-router-dom';

/**
 * @interface AppHeaderProps
 * @description Defines the props for the AppHeader component.
 * @property {number} trailCount - The total number of trails, used for display in the "Mes pistes" button.
 */
interface AppHeaderProps {
  trailCount: number;
  onOpenTrailNavigation?: () => void;
}

/**
 * @function AppHeader
 * @description React functional component that renders the application's header.
 * It includes navigation links, a user dropdown menu with profile management options,
 * and a `ProfileSwitcher` for trainer users to toggle between different view modes.
 * @param {AppHeaderProps} props - The component props.
 */
export function AppHeader({
  trailCount,
  onOpenTrailNavigation,
}: AppHeaderProps) {
  const { user, logout } = useAuth();
  const { viewMode } = useViewMode();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * @function handleLogout
   * @description Handles the user logout process.
   */
  const handleLogout = () => {
    localStorage.setItem("viewMode", "user");
    logout();
    navigate("/login"); // Redirect to login page after logout
  };

  /**
   * @function getUserInitials
   * @description Generates user initials from the username for display in the avatar fallback.
   * @returns {string} The user's initials.
   */
  const getUserInitials = () => {
    console.log("getUserInitials: user =", user);
    if (!user || !user.username) return "U";
    const nameParts = user.username.split(" ");
    if (nameParts.length === 0) return "U";
    const firstInitial = nameParts[0][0] || "";
    const lastInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  /**
   * @function getUserFullName
   * @description Constructs the user's full name from firstName and lastName.
   * @param {any} user - The user object.
   * @returns {string} The user's full name or "Utilisateur Inconnu".
   */
  const getUserFullName = (user: any) => {
    if (!user || (!user.firstName && !user.lastName)) return "Utilisateur Inconnu";
    return `${user.firstName || ""} ${user.lastName || ""}`.trim().toUpperCase();
  };

  return (
    <header className="no-print bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white shadow-lg">
      <div className="flex min-h-14 items-center justify-between gap-2 px-3 py-2 sm:px-6 sm:py-4">
        {/* Logo & Title Section */}
        <div className="flex min-w-0 shrink items-center gap-2 sm:gap-3">
          {onOpenTrailNavigation && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="app-header__mobile-menu shrink-0 text-white hover:bg-white/10"
              aria-label="Ouvrir la navigation des activités"
              onClick={onOpenTrailNavigation}
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <div className="shrink-0 rounded-lg bg-white p-1 shadow-md">
            <TitusLogo size={32} />
          </div>
          <div className="app-header__brand-copy min-w-0">
            <h1 className="text-lg sm:text-xl">Carnet de Titus</h1>
            <p className="text-xs text-blue-100 opacity-90">
              Mantrailing & Randonnée
            </p>
          </div>
        </div>

        {/* Navigation Links */}
        <nav
          aria-label="Navigation principale"
          className="app-header__nav flex shrink-0 items-center"
        >
          {viewMode === "user" && (
            <Link to="/home">
              <Button
                variant={location.pathname === "/home" ? "secondary" : "ghost"}
                aria-label="Accueil"
                className={
                  location.pathname === "/home"
                    ? "gap-2 bg-white text-blue-700 hover:bg-blue-50"
                    : "gap-2 text-white hover:bg-white/10"
                }
              >
                <Home className="h-4 w-4" />
                <span className="hidden md:inline">Accueil</span>
              </Button>
            </Link>
          )}

          {viewMode === "trainer" && (
            <Link to="/trainer-dashboard">
              <Button
                variant={location.pathname === "/trainer-dashboard" ? "secondary" : "ghost"}
                aria-label="Mes élèves"
                className={
                  location.pathname === "/trainer-dashboard"
                    ? "gap-2 bg-white text-blue-700 hover:bg-blue-50"
                    : "gap-2 text-white hover:bg-white/10"
                }
              >
                <Users className="h-4 w-4" />
                <span className="hidden md:inline">Mes élèves</span>
              </Button>
            </Link>
          )}

          {viewMode === "trainer" && (
            <Link to="/sessions">
              <Button
                variant={location.pathname.startsWith("/sessions") ? "secondary" : "ghost"}
                aria-label="Sessions"
                className={
                  location.pathname.startsWith("/sessions")
                    ? "gap-2 bg-white text-blue-700 hover:bg-blue-50"
                    : "gap-2 text-white hover:bg-white/10"
                }
              >
                <Calendar className="h-4 w-4" />
                <span className="hidden md:inline">Sessions</span>
              </Button>
            </Link>
          )}

          {viewMode === "user" && trailCount > 0 && (
            <>
              <Link to="/trails">
                <Button
                  aria-label={`Mes pistes (${trailCount})`}
                  variant={
                    location.pathname.startsWith("/trails")
                      ? "secondary"
                      : "ghost"
                  }
                  className={
                    location.pathname.startsWith("/trails")
                      ? "gap-2 bg-white text-blue-700 hover:bg-blue-50"
                      : "gap-2 text-white hover:bg-white/10"
                  }
                >
                  <List className="h-4 w-4" />
                  <span className="hidden md:inline">Mes pistes</span>
                  <span className="ml-1 hidden text-xs opacity-70 md:inline">({trailCount})</span>
                </Button>
              </Link>

              <Link to="/statistics">
                <Button
                  variant={location.pathname === "/statistics" ? "secondary" : "ghost"}
                  aria-label="Statistiques"
                  className={
                    location.pathname === "/statistics"
                      ? "gap-2 bg-white text-blue-700 hover:bg-blue-50"
                      : "gap-2 text-white hover:bg-white/10"
                  }
                >
                  <BarChart3 className="h-4 w-4" />
                  <span className="hidden md:inline">Statistiques</span>
                </Button>
              </Link>

              <Link to="/badges">
                <Button
                  variant={location.pathname === "/badges" ? "secondary" : "ghost"}
                  aria-label="Badges"
                  className={
                    location.pathname === "/badges"
                      ? "gap-2 bg-white text-blue-700 hover:bg-blue-50"
                      : "gap-2 text-white hover:bg-white/10"
                  }
                >
                  <Award className="h-4 w-4" />
                  <span className="hidden md:inline">Badges</span>
                </Button>
              </Link>

              {SESSION_PROPOSALS_ENABLED && <Link to="/proposals">
                <Button
                  variant={location.pathname === "/proposals" ? "secondary" : "ghost"}
                  aria-label="Sessions proposées"
                  className={
                    location.pathname === "/proposals"
                      ? "gap-2 bg-white text-blue-700 hover:bg-blue-50"
                      : "gap-2 text-white hover:bg-white/10"
                  }
                >
                  <Calendar className="h-4 w-4" />
                  <span className="hidden md:inline">Sessions</span>
                </Button>
              </Link>}
            </>
          )}

          {/* Admin Button */}
          {user?.role.includes("admin") && (
            <Link to="/admin">
              <Button
                variant={location.pathname === "/admin" ? "secondary" : "ghost"}
                aria-label="Admin"
                className={
                  location.pathname === "/admin"
                    ? "gap-2 bg-white text-red-700 hover:bg-red-50"
                    : "gap-2 text-white hover:bg-white/10"
                }
              >
                <Shield className="h-4 w-4" />
                <span className="hidden md:inline">Admin</span>
              </Button>
            </Link>
          )}
          
          {/* User Menu Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                aria-label="Menu utilisateur"
                className="app-header__user-button gap-0 text-white hover:bg-white/10"
              >
                <Avatar className="app-header__avatar">
                  <AvatarFallback className="bg-white text-blue-700">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden md:inline">{user?.username}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{getUserFullName(user!)}</span>
                  <span className="text-xs text-muted-foreground">
                    {user?.email}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* ProfileSwitcher is only visible for trainer users */}
              {user?.role && user.role.includes("trainer")&& (
                <DropdownMenuItem className="p-0" onSelect={(e) => e.preventDefault()}>
                  <ProfileSwitcher />
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate("/profile")}>
                <User className="mr-2 h-4 w-4" />
                Mon profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/management")}>
                <Settings className="mr-2 h-4 w-4" />
                Gestion (Chiens)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>
      </div>
    </header>
  );
}


